import { FC, useEffect, useMemo } from 'react';

import { useFormik, FormikProvider, Form } from 'formik';
import * as yup from 'yup';

import { i18n as I18n } from 'utils/i18n';

import Field from '../fields/field';
import TextBox from '../fields/textbox';
import SelectList from '../fields/selectList';

interface BookingHost {
  id: number;
  email: string;
}

yup.addMethod(
  yup.string,
  'uniqueEmail',
  // eslint-disable-next-line func-names
  function (hosts: BookingHost[] = [], hostId?: number = undefined) {
    return this.test('unique', (value, { createError }) => {
      if (!value) {
        return true;
      }

      if (hosts.some(({ id, email }) => email === value && id !== hostId)) {
        return createError({
          message: I18n.t('booking_hosts.accounts.form.unique_email'),
        });
      }

      return true;
    });
  }
);

interface Values {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
  telephone: string;
  active: boolean;
}

interface Props {
  initialValues?: Values;
  bookingHosts?: BookingHost[];
  onSubmit?: (any) => any;
  onChange?: (any) => any;
  editBookingHost?: boolean;
}

const defaultInitialValues = {
  firstName: '',
  lastName: '',
  email: '',
  telephone: '',
  active: true,
};

const BookingHostForm: FC<Props> = ({
  initialValues = defaultInitialValues,
  bookingHosts = [],
  onSubmit = () => null,
  onChange = () => null,
  editBookingHost = false,
  ...props
}) => {
  const emailSchema = useMemo(
    () =>
      yup
        .string()
        .email()
        .max(200)
        .required(
          I18n.t('validations.required', {
            name: I18n.t('booking_hosts.form.fields.email.label'),
          })
        )
        .uniqueEmail(bookingHosts, initialValues.id),
    [bookingHosts, initialValues.id]
  );

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        firstName: yup
          .string()
          .trim()
          .max(100)
          .required(
            I18n.t('validations.required', {
              name: I18n.t('booking_hosts.form.fields.first_name.label'),
            })
          ),
        lastName: yup
          .string()
          .trim()
          .max(100)
          .required(
            I18n.t('validations.required', {
              name: I18n.t('booking_hosts.form.fields.last_name.label'),
            })
          ),
        email: emailSchema,
        telephone: yup
          .string()
          .matches(
            /[\d\W]{7,20}/,
            I18n.t('booking_hosts.form.validations.telephone')
          )
          .required(
            I18n.t('validations.required', {
              name: I18n.t('booking_hosts.form.fields.telephone.label'),
            })
          ),
        active: yup.bool().required(),
      }),
    [emailSchema]
  );

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnMount: true,
  });

  const { errors, touched, isValid } = formik;

  useEffect(() => {
    onChange(isValid);
  }, [onChange, isValid]);

  const introText = editBookingHost
    ? I18n.t('booking_hosts.edit_modal.intro')
    : I18n.t('booking_hosts.modal.intro');

  return (
    <FormikProvider value={formik}>
      <Form
        data-testid="bookingHostForm"
        id="bookingHostForm"
        className="booking-host-form"
        {...props}
      >
        <p data-testid="bookingHostsModalIntro">{introText}</p>
        <Field
          as={TextBox}
          type="text"
          label={I18n.t('booking_hosts.form.fields.first_name.label')}
          name="firstName"
          maxLength={100}
          hideLabel
          placeholder={I18n.t(
            'booking_hosts.form.fields.first_name.placeholder'
          )}
          error={touched.firstName && !!errors.firstName}
          errorMsg={errors.firstName}
        />
        <Field
          as={TextBox}
          type="text"
          label={I18n.t('booking_hosts.form.fields.last_name.label')}
          name="lastName"
          maxLength={100}
          hideLabel
          placeholder={I18n.t(
            'booking_hosts.form.fields.last_name.placeholder'
          )}
          error={touched.lastName && !!errors.lastName}
          errorMsg={errors.lastName}
        />
        <Field
          as={TextBox}
          type="text"
          label={I18n.t('booking_hosts.form.fields.email.label')}
          name="email"
          maxLength={200}
          hideLabel
          placeholder={I18n.t('booking_hosts.form.fields.email.placeholder')}
          error={touched.email && !!errors.email}
          errorMsg={errors.email}
        />
        <Field
          as={TextBox}
          type="text"
          label={I18n.t('booking_hosts.form.fields.telephone.label')}
          name="telephone"
          maxLength={20}
          hideLabel
          placeholder={I18n.t(
            'booking_hosts.form.fields.telephone.placeholder'
          )}
          error={touched.telephone && !!errors.telephone}
          errorMsg={errors.telephone}
        />
        {editBookingHost && (
          <Field
            as={SelectList}
            name="active"
            label="Active"
            setValueProp="onChange"
            options={[
              { value: true, label: 'Active' },
              { value: false, label: 'Inactive' },
            ]}
          />
        )}
      </Form>
    </FormikProvider>
  );
};

export default BookingHostForm;
